// BalanceNumber Component
// Число баланса — как справляться с трудностями

import React from 'react';

interface BalanceNumberData {
  number: number;
  description: string;
  interpretation: string;
  aiInterpretation?: string;
  copingStrategies: string[];
  challenges: string[];
  recommendations: string[];
}

interface BalanceNumberProps {
  data: BalanceNumberData;
  loading?: boolean;
}

const BALANCE_COLOR = '#00897B';

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 25, 48, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 137, 123, 0.3)',
  borderRadius: '20px',
  padding: '24px',
  marginBottom: '20px',
};

const numberStyle: React.CSSProperties = {
  fontSize: 'clamp(48px, 8vw, 72px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 700,
  textAlign: 'center',
  margin: '16px 0',
  textShadow: '0 0 30px rgba(0, 137, 123, 0.5)',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#00897B',
  fontSize: '16px',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 600,
  marginBottom: '12px',
  marginTop: '20px',
};

const textStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  color: '#d0d0d0',
  fontSize: '14px',
  lineHeight: 1.6,
  fontFamily: 'var(--font-sans)',
  paddingLeft: '20px',
  marginBottom: '6px',
  position: 'relative' as const,
};

const aiTextStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  whiteSpace: 'pre-wrap',
  padding: '16px',
  background: 'rgba(0, 137, 123, 0.1)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 137, 123, 0.2)',
};

const bulletStyle: React.CSSProperties = {
  position: 'absolute' as const,
  left: '4px',
  color: '#00897B',
};

const BalanceNumber: React.FC<BalanceNumberProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: '#888' }}>Расчёт числа баланса...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Главное число */}
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <h3 style={{
          color: BALANCE_COLOR,
          fontSize: '20px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          marginBottom: '8px',
        }}>
          ⚖️ Число баланса
        </h3>

        <div style={{ fontSize: '48px', marginBottom: '8px' }}>⚖️</div>

        <p style={{
          color: '#e0e0e0',
          fontSize: '14px',
          lineHeight: 1.7,
          fontFamily: 'var(--font-sans)',
        }}>
          Число баланса показывает, как вы справляетесь с трудностями и стрессом.
          Это ваша естественная стратегия восстановления равновесия в сложные времена.
        </p>

        {/* Большое число баланса */}
        <div style={{ ...numberStyle, color: BALANCE_COLOR }}>
          {data.number}
        </div>

        {/* Описание */}
        {data.description && (
          <p style={textStyle}>{data.description}</p>
        )}

        {/* Статическая интерпретация */}
        {data.interpretation && (
          <div style={{ marginTop: '20px' }}>
            <div style={sectionTitleStyle}>📖 Как вы восстанавливаете баланс:</div>
            <p style={textStyle}>{data.interpretation}</p>
          </div>
        )}

        {/* Стратегии преодоления */}
        {data.copingStrategies && data.copingStrategies.length > 0 && (
          <>
            <div style={sectionTitleStyle}>🛡️ Ваши стратегии преодоления:</div>
            {data.copingStrategies.map((strategy, i) => (
              <div key={i} style={listItemStyle}>
                <span style={bulletStyle}>•</span>
                {strategy}
              </div>
            ))}
          </>
        )}

        {/* Вызовы */}
        {data.challenges && data.challenges.length > 0 && (
          <>
            <div style={sectionTitleStyle}>⚡ Типичные вызовы:</div>
            {data.challenges.map((challenge, i) => (
              <div key={i} style={{ ...listItemStyle, color: '#FFAA44' }}>
                <span style={{ ...bulletStyle, color: '#FFAA44' }}>•</span>
                {challenge}
              </div>
            ))}
          </>
        )}

        {/* Рекомендации */}
        {data.recommendations && data.recommendations.length > 0 && (
          <>
            <div style={sectionTitleStyle}>🌟 Рекомендации:</div>
            {data.recommendations.map((rec, i) => (
              <div key={i} style={listItemStyle}>
                <span style={bulletStyle}>→</span>
                {rec}
              </div>
            ))}
          </>
        )}
      </div>

      {/* AI-интерпретация */}
      {data.aiInterpretation && (
        <div style={cardStyle}>
          <h4 style={{
            color: BALANCE_COLOR,
            fontSize: '16px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '12px',
          }}>
            ⚖️ Интерпретация
          </h4>
          <div style={aiTextStyle}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceNumber;
